import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineExclamation,
  HiOutlineKey,
  HiOutlineLockClosed,
  HiOutlineRefresh,
} from "react-icons/hi";

import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { CopyToClipboardButton } from "../../../atoms/CopyToClipboardButton";
import {
  WebhookSecretInfo,
  WebhookSecretRevealed,
  useFetchWebhookSecret,
  useRotateWebhookSecret,
} from "../../../../hooks/entity";

type WebhookSecretModalProps = {
  show: boolean;
  onClose: () => void;
  customer_identification: string;
  entity_identification: string;
};

type View = "initial" | "loading" | "info" | "revealed" | "confirmRotate";

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const WebhookSecretModal = ({
  show,
  onClose,
  customer_identification,
  entity_identification,
}: WebhookSecretModalProps) => {
  const { t } = useTranslation();
  const prefix = "dashboard.entities.entityPreferences.webhookSecret";

  const [view, setView] = useState<View>("initial");
  const [info, setInfo] = useState<WebhookSecretInfo | null>(null);
  const [revealed, setRevealed] = useState<WebhookSecretRevealed | null>(null);

  const { mutate: fetchSecret, isLoading: isFetching } =
    useFetchWebhookSecret();
  const { mutate: rotateSecret, isLoading: isRotating } =
    useRotateWebhookSecret();

  useEffect(() => {
    if (!show) {
      setView("initial");
      setInfo(null);
      setRevealed(null);
    }
  }, [show]);

  const handleFetch = () => {
    setView("loading");
    fetchSecret(
      { customer_identification, entity_identification },
      {
        onSuccess: (data) => {
          setInfo(data);
          setView("info");
        },
        onError: () => setView("initial"),
      }
    );
  };

  const handleGenerateOrRotate = () => {
    rotateSecret(
      { customer_identification, entity_identification },
      {
        onSuccess: (data) => {
          setRevealed(data);
          setView("revealed");
        },
        onError: () => {
          if (info) setView("info");
          else setView("initial");
        },
      }
    );
  };

  const isConfigured = !!info?.secret_preview;
  const isBusy = isFetching || isRotating;

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="2xl"
      dismissible={!isBusy}
      title={
        <span className="flex items-center gap-2">
          <HiOutlineLockClosed className="w-5 h-5 text-blue-600" />
          {t(`${prefix}.title`)}
        </span>
      }
    >
      <div className="space-y-5 text-sm text-gray-600 leading-relaxed">
        {view === "initial" && (
          <>
            <p>{t(`${prefix}.intro`)}</p>
            <p className="text-xs text-gray-500">
              {t(`${prefix}.privacyNote`)}
            </p>
            <div className="flex justify-end pt-2">
              <CustomButton
                variant="primary"
                onClick={handleFetch}
                isLoading={isFetching}
                disabled={isFetching}
                title={
                  <span className="inline-flex items-center gap-2">
                    <HiOutlineKey className="w-4 h-4" />
                    {t(`${prefix}.viewSecret`)}
                  </span>
                }
              />
            </div>
          </>
        )}

        {view === "loading" && (
          <div className="flex items-center justify-center py-8">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <span className="ml-3 text-gray-500">{t(`${prefix}.loading`)}</span>
          </div>
        )}

        {view === "info" && info && (
          <>
            {isConfigured ? (
              <>
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        {t(`${prefix}.currentSecret`)}
                      </p>
                      <p
                        className="font-mono text-sm text-gray-800 mt-0.5"
                        translate="no"
                      >
                        {info.secret_preview}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      {t(`${prefix}.statusActive`)}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 pt-2 border-t border-gray-200">
                    <dt className="text-gray-500">
                      {t(`${prefix}.algorithm`)}
                    </dt>
                    <dd className="font-mono text-gray-800" translate="no">
                      {info.algorithm}
                    </dd>
                    <dt className="text-gray-500">
                      {t(`${prefix}.createdAt`)}
                    </dt>
                    <dd>{formatDate(info.created_at)}</dd>
                    <dt className="text-gray-500">
                      {t(`${prefix}.lastRotatedAt`)}
                    </dt>
                    <dd>{formatDate(info.last_rotated_at)}</dd>
                  </dl>
                </div>
                <p className="text-xs text-gray-500">
                  {t(`${prefix}.previewOnlyNote`)}
                </p>
                <div className="flex justify-end gap-2 pt-2">
                  <CustomButton
                    variant="secondary"
                    onClick={() => setView("confirmRotate")}
                    disabled={isBusy}
                    title={
                      <span className="inline-flex items-center gap-2">
                        <HiOutlineRefresh className="w-4 h-4" />
                        {t(`${prefix}.rotate`)}
                      </span>
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-2">
                    <HiOutlineExclamation className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-amber-800 font-medium">
                        {t(`${prefix}.notConfiguredTitle`)}
                      </p>
                      <p className="text-amber-700 text-xs mt-1">
                        {t(`${prefix}.notConfiguredBody`)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <CustomButton
                    variant="primary"
                    onClick={handleGenerateOrRotate}
                    isLoading={isRotating}
                    disabled={isRotating}
                    title={
                      <span className="inline-flex items-center gap-2">
                        <HiOutlineKey className="w-4 h-4" />
                        {t(`${prefix}.generate`)}
                      </span>
                    }
                  />
                </div>
              </>
            )}
          </>
        )}

        {view === "confirmRotate" && (
          <>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <HiOutlineExclamation className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-amber-800 font-medium">
                    {t(`${prefix}.confirmRotateTitle`)}
                  </p>
                  <p className="text-amber-700 text-xs">
                    {t(`${prefix}.confirmRotateBody`)}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <CustomButton
                variant="outline"
                onClick={() => setView("info")}
                disabled={isRotating}
                title={t(`${prefix}.cancel`) as string}
              />
              <CustomButton
                variant="primary"
                onClick={handleGenerateOrRotate}
                isLoading={isRotating}
                disabled={isRotating}
                title={
                  <span className="inline-flex items-center gap-2">
                    <HiOutlineRefresh className="w-4 h-4" />
                    {t(`${prefix}.rotateConfirmBtn`)}
                  </span>
                }
              />
            </div>
          </>
        )}

        {view === "revealed" && revealed && (
          <>
            <div className="rounded-md border-2 border-red-300 bg-red-50 p-4">
              <div className="flex items-start gap-2">
                <HiOutlineExclamation className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-red-800 font-semibold">
                    {t(`${prefix}.oneTimeTitle`)}
                  </p>
                  <p className="text-red-700 text-xs">
                    {t(`${prefix}.oneTimeBody`)}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                {t(`${prefix}.yourSecret`)}
              </p>
              <div className="rounded-md border border-gray-300 bg-gray-900 p-3 overflow-x-auto">
                <code
                  className="text-xs text-green-300 font-mono break-all"
                  translate="no"
                >
                  {revealed.secret}
                </code>
              </div>
              <div className="flex justify-end mt-2">
                <CopyToClipboardButton
                  textToCopy={revealed.secret}
                  variant="primary"
                  label={t(`${prefix}.copySecret`) as string}
                />
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 pt-2 border-t border-gray-200">
              <dt className="text-gray-500">{t(`${prefix}.algorithm`)}</dt>
              <dd className="font-mono text-gray-800" translate="no">
                {revealed.algorithm}
              </dd>
              <dt className="text-gray-500">{t(`${prefix}.createdAt`)}</dt>
              <dd>{formatDate(revealed.created_at)}</dd>
              <dt className="text-gray-500">{t(`${prefix}.lastRotatedAt`)}</dt>
              <dd>{formatDate(revealed.last_rotated_at)}</dd>
            </dl>

            <div className="flex justify-end pt-2">
              <CustomButton
                variant="primary"
                onClick={onClose}
                title={t(`${prefix}.savedClose`) as string}
              />
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
};

export default WebhookSecretModal;
